import { ChangeDetectorRef, Component, ElementRef, Inject, inject, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import {
  AbstractPaymentMethod,
  PAYMENT_METHOD_CATEGORY,
  PAYMENT_METHOD_CATEGORY_LOG_NAME,
  PAYMENT_METHOD_TYPE_TO_CATEGORY,
  PaymentFlow,
  PaymentMethodIntent,
  PaymentMethodMode,
  PaymentPlatformService,
  StripeSelectNewPaymentMethodComponent,
} from '../../platforms';
import { catchError, distinctUntilChanged, finalize, map, pairwise, shareReplay, skip, startWith, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, defer, Observable, of, Subject } from 'rxjs';
import { Store } from '@ngxs/store';
import {
  ClearNewPaymentMethodData,
  PaymentsState,
  SetShouldSaveNewPaymentMethod,
  SetShouldSetNewPaymentMethodAsDefault,
} from '../../state';
import {
  areStringsEqualIgnoreCase,
  ConfirmationModalComponent,
  ConfirmationPayload,
  DialogSize,
  extractFailureContext,
  getControlValueChanges,
  getLoadedResults,
  ignoreEmpty,
  ignoreNullish,
  ignoreTruthy,
  invert,
  isEmpty,
  Logger,
  mapToAndResult,
  replaceArrayItem,
  TrackService,
} from '@aiwyn/shared';
import {
  AIWYN_VALIDATION_ERROR_CATEGORY,
  PAYMENT_METHOD_VALIDATION_ERROR_PRECEDENCE,
  PaymentMethodSurface,
  PaymentMethodTrackService,
  PaymentMethodWorkflowEvent,
} from '../../tracking';
import { AuthAdaptorService, AuthStateAdaptorService, UserType } from '@aiwyn/auth';
import { UntypedFormControl } from '@angular/forms';
import { CardFunding, CurrencyCode, PaymentFeeType, PaymentMethodType, PaymentMethodValidationError, PaymentMethodValidationErrors, PaymentPlatform } from '@aiwyn/api';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfigService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { CommonSettingsState } from '@aiwyn/common/settings';
import { PaymentMethodError } from '../../components/new-payment-method-failed/types';

export type NewPaymentMethodDialogData = NewPaymentMethodDialogPaymentData | NewPaymentMethodDialogSetupData;

interface NewPaymentMethodDialogBaseData {
  userHasSavedPaymentMethods: boolean;
  intent: PaymentMethodIntent;
  /**
   * Where the dialog was opened from. Stamped on every PAYMENT_METHOD_* event via the
   * `PaymentMethodTrackingContextService` envelope so dashboards can attribute the add flow
   * to its origin (settings vs. checkout vs. engagement vs. deliverables).
   */
  surface: PaymentMethodSurface;
}

interface NewPaymentMethodDialogPaymentData extends NewPaymentMethodDialogBaseData {
  intent: PaymentMethodIntent.PAYMENT;
  paymentFlow: PaymentFlow;
}

interface NewPaymentMethodDialogSetupData extends NewPaymentMethodDialogBaseData {
  intent: PaymentMethodIntent.SETUP;
}

export interface NewPaymentMethodDialogResult {
  createdPaymentMethod: AbstractPaymentMethod;
  isTemporary: boolean;
}

export type NewPaymentMethodDialogRef = MatDialogRef<NewPaymentMethodDialogComponent, NewPaymentMethodDialogResult>;

type PlatformSelectNewPaymentMethodComponent = StripeSelectNewPaymentMethodComponent; // | OtherPaymentPlatformSelectNewPaymentMethodComponent;

const HAS_USER_DISMISSED_DEBIT_BLOCK_WARNING = 'hasUserDismissedDebitBlockWarning';

export interface SecureAchInfoMessage {
  benefit: string;
  explanation: string;
}

const SECURE_ACH_INFO_MESSAGES: SecureAchInfoMessage[] = [
  {
    benefit: 'Your bank credentials remain confidential',
    explanation: 'because we never see or store your login information.',
  },
  {
    benefit: 'Your account numbers stay private',
    explanation: 'because we only receive a secure token, never your banking details.',
  },
  {
    benefit: 'Your transactions are never stored or synced',
    explanation: 'so your financial activity stays between you and your bank.',
  },
  {
    benefit: 'Your data is protected by advanced encryption',
    explanation: 'the same standard trusted by your bank.',
  },
  {
    benefit: 'Avoid payment errors and delays',
    explanation: 'by preventing mistakes from manual entry.',
  },
];

interface DynamicFeesInfoBlockSection {
  currencyCode: CurrencyCode;
  fees: DynamicFeesInfoBlockFee[];
}

interface DynamicFeesInfoBlockFee {
  type: string;
  value: number;
}

const PAYMENT_FEE_TYPE_DISPLAY: Partial<Record<PaymentFeeType, string>> = {
  [PaymentFeeType.CREDIT_CARD_PROCESSING_FEE_RATE]: 'Processing fee',
  [PaymentFeeType.PAYMENT_PLAN_CREDIT_CARD_PROCESSING_FEE_RATE]: 'Scheduled payments processing fee',
  [PaymentFeeType.INTERNATIONAL_FEE_RATE]: 'International fee — when applicable',
};

const PAYMENT_FEE_TYPE_ORDER: Partial<Record<PaymentFeeType, number>> = {
  [PaymentFeeType.CREDIT_CARD_PROCESSING_FEE_RATE]: 1,
  [PaymentFeeType.PAYMENT_PLAN_CREDIT_CARD_PROCESSING_FEE_RATE]: 2,
  [PaymentFeeType.INTERNATIONAL_FEE_RATE]: 3,
};

@Component({
  selector: 'app-new-payment-method-dialog',
  templateUrl: './new-payment-method-dialog.component.html',
  styleUrls: ['./new-payment-method-dialog.component.scss'],
  standalone: false,
  providers: [PaymentMethodTrackService],
})
export class NewPaymentMethodDialogComponent implements OnDestroy {
  PaymentPlatform = PaymentPlatform;
  PaymentMethodIntent = PaymentMethodIntent;
  DialogSize = DialogSize;

  private readonly _unsubscribe$ = new Subject<void>();

  private readonly dialog = inject(MatDialog);

  private readonly paymentMethodTrackService = inject(PaymentMethodTrackService);
  // Reserved for `_logPaymentMethod`'s deprecated event names ("Card Payment Method" etc.)
  // — those don't fit the workflow taxonomy and stay on the generic `TrackService.track()`
  // surface until dashboard owners confirm retirement.
  private readonly legacyTrackService = inject(TrackService);

  @ViewChildren('platformSelectNewPaymentMethodTemplate')
  platformSelectNewPaymentMethodTemplate: QueryList<PlatformSelectNewPaymentMethodComponent>;

  platform$ = this.paymentPlatformService.platform;

  setPaymentMethodModeAsNew$ = this.paymentPlatformService.setPaymentMethodMode(PaymentMethodMode.NEW).pipe(
    take(1),
    takeUntil(this._unsubscribe$),
  );

  cardRestrictionsHint$ = this.paymentPlatformService.getCardRestrictionsHint();

  processingFeeHint$ = defer(() => {
    if (this.data.intent === PaymentMethodIntent.SETUP) {
      return of(undefined);
    }

    return this.store.select(PaymentsState.getActiveCurrency).pipe(
      switchMap(activeCurrency => this.paymentPlatformService.getProcessingFeeHint(PaymentsState.getPaymentFeeRate(
        'paymentFlow' in this.data && this.data.paymentFlow === PaymentFlow.STANDARD // `in` is needed even though `intent` is checked
          ? PaymentFeeType.CREDIT_CARD_PROCESSING_FEE_RATE
          : PaymentFeeType.PAYMENT_PLAN_CREDIT_CARD_PROCESSING_FEE_RATE,
        CardFunding.CREDIT,
        activeCurrency.code,
      ))),
    );
  });

  canShowAdditionalActionCheckboxes$ = this.paymentPlatformService.getNewPaymentMethodType().pipe(
    map(newPaymentMethodType => this.authAdaptor.isContactOrAdmin && newPaymentMethodType != undefined),
    startWith(false),
  );

  saveCheckboxControl = new UntypedFormControl(this.isSaveRequired());
  setAsDefaultCheckboxControl = new UntypedFormControl(false);

  manageCheckboxStates$ = combineLatest([
    getControlValueChanges<boolean>(this.saveCheckboxControl).pipe(
      skip(1),
      tap(shouldSave => this.store.dispatch(new SetShouldSaveNewPaymentMethod(shouldSave))),
    ),
    getControlValueChanges<boolean>(this.setAsDefaultCheckboxControl).pipe(
      skip(1),
      tap(shouldSetAsDefault => this.store.dispatch(new SetShouldSetNewPaymentMethodAsDefault(shouldSetAsDefault))),
    ),
  ]).pipe(
    pairwise(),
    tap(([[previousShouldSave, previousShouldSetAsDefault], [currentShouldSave, currentShouldSetAsDefault]]) => {
      // if the user unchecks the save checkbox, the set as default checkbox should be unchecked
      if (previousShouldSave && !currentShouldSave && currentShouldSetAsDefault) {
        this.setAsDefaultCheckboxControl.setValue(false);
      }

      // if the user checks the set as default checkbox, the save checkbox should be checked
      if (!previousShouldSetAsDefault && currentShouldSetAsDefault && !currentShouldSave) {
        this.saveCheckboxControl.setValue(true);
      }
    }),
    takeUntil(this._unsubscribe$),
  );

  userProvidedEmail$ = this.authStateAdaptor.getUserEmail().pipe(
    take(1),
    ignoreTruthy(),
    switchMap(_ => this.store.select(PaymentsState.getProvidedPrimaryRecipientEmail)),
    // passing undefined signals to the Stripe payment element that it will be populated externally, so avoid that here
    map(userProvidedEmail => userProvidedEmail ?? ''),
  );

  areCardsAccepted$ = this.paymentPlatformService.getNewPaymentMethodTypeOptions().pipe(
    map(newPaymentMethodTypeOptions => newPaymentMethodTypeOptions.includes(PaymentMethodType.CARD)),
    shareReplay(1),
  );

  isAchSelected$ = this.paymentPlatformService.getNewPaymentMethodType().pipe(
    map(newPaymentMethodType => [PaymentMethodType.ACH, PaymentMethodType.US_BANK_ACCOUNT].includes(newPaymentMethodType)),
    shareReplay(1),
  );

  isCardSelected$ = this.paymentPlatformService.getNewPaymentMethodType().pipe(
    map(newPaymentMethodType => newPaymentMethodType === PaymentMethodType.CARD),
    shareReplay(1),
  );

  paymentFeeConfigs$ = getLoadedResults(this.store.select(PaymentsState.getPaymentFeeConfigsLoadable)).pipe(
    shareReplay(1),
  );

  creditCardPaymentFeeConfigs$ = this.paymentFeeConfigs$.pipe(
    map(paymentFeeConfigs => paymentFeeConfigs.filter(fee => areStringsEqualIgnoreCase(fee.paymentMethodType, CardFunding.CREDIT))),
    shareReplay(1),
  );

  doMultipleCurrenciesExist$ = this.store.select(CommonSettingsState.doMultipleClientCurrenciesExist).pipe(
    ignoreNullish(),
    shareReplay(1),
  );

  doesRelevantCreditCardFeeConfigExist$ = defer(() => {
    if (this.data.intent === PaymentMethodIntent.SETUP) {
      return of(undefined);
    }

    return this.creditCardPaymentFeeConfigs$.pipe(
      map(creditCardPaymentFeeConfigs => {
        const targetFeeType = (this.data as NewPaymentMethodDialogPaymentData).paymentFlow === PaymentFlow.STANDARD
          ? PaymentFeeType.CREDIT_CARD_PROCESSING_FEE_RATE
          : PaymentFeeType.PAYMENT_PLAN_CREDIT_CARD_PROCESSING_FEE_RATE;

        return !isEmpty(creditCardPaymentFeeConfigs?.filter(({ type }) => type === targetFeeType));
      }),
    );
  });

  canShowDynamicFeesInfoBlock$ = combineLatest([
    this.doMultipleCurrenciesExist$,
    this.doesRelevantCreditCardFeeConfigExist$,
  ]).pipe(
    mapToAndResult(),
    shareReplay(1),
  );

  shouldShowProcessingFeeHint$ = combineLatest([
    this.areCardsAccepted$,
    this.canShowDynamicFeesInfoBlock$.pipe(invert()), // essentially a toggle between this hint and the dynamic fees info block
    this.isCardSelected$,
  ]).pipe(
    mapToAndResult(),
  );

  shouldShowDynamicFeesInfoBlock$ = combineLatest([
    this.areCardsAccepted$,
    this.canShowDynamicFeesInfoBlock$,
    this.isCardSelected$,
  ]).pipe(
    mapToAndResult(),
  );

  /**
   * Get the currencies of the clients the user is connected to, and combine them with the default payment fees.
   */
  private getFeeTypeDisplay(type: PaymentFeeType): string {
    switch (type) {
      case PaymentFeeType.CREDIT_CARD_PROCESSING_FEE_RATE:
        return this.configService.processingFeeLabel;
      case PaymentFeeType.PAYMENT_PLAN_CREDIT_CARD_PROCESSING_FEE_RATE:
        return `Scheduled payments ${this.configService.processingFeeLabel.replace(/^[A-Z]/, c => c.toLowerCase())}`;
      default:
        return PAYMENT_FEE_TYPE_DISPLAY[type] ?? '';
    }
  }

  defaultPaymentFees$: Observable<DynamicFeesInfoBlockSection[]> = this.store.select(CommonSettingsState.getClientCurrenciesData).pipe(
    ignoreEmpty(),
    map(clientCurrencies => {
      const defaultFees: DynamicFeesInfoBlockFee[] = [
        {
          type: this.getFeeTypeDisplay(PaymentFeeType.CREDIT_CARD_PROCESSING_FEE_RATE),
          value: this.configService.customerCreditCardProcessingFeeRate,
        },
        {
          type: this.getFeeTypeDisplay(PaymentFeeType.PAYMENT_PLAN_CREDIT_CARD_PROCESSING_FEE_RATE),
          value: this.configService.paymentPlanProcessingFeeRate,
        },
        { type: this.getFeeTypeDisplay(PaymentFeeType.INTERNATIONAL_FEE_RATE), value: this.configService.internationalCardFeeRate },
      ];

      return clientCurrencies.sort().map(currency => ({ currencyCode: currency.code, fees: defaultFees }));
    }),
  );

  dynamicFeesInfoBlockContent$: Observable<DynamicFeesInfoBlockSection[]> = combineLatest([
    this.defaultPaymentFees$,
    this.creditCardPaymentFeeConfigs$,
  ]).pipe(
    map(([defaultPaymentFees, creditCardPaymentFeeConfigs]) => {
      const dynamicFeesInfoBlockContent = creditCardPaymentFeeConfigs.reduce(
        (acc, config) => {
          const matchingSectionIndex = acc.findIndex(section => section.currencyCode === config.currencyCode);
          if (matchingSectionIndex === -1) {
            return acc; // we only want to show fees for the currencies of the clients the user is connected to (mouth full)
          }

          const matchingFees: DynamicFeesInfoBlockFee[] = matchingSectionIndex === -1 ? [] : acc[matchingSectionIndex].fees;
          const matchingFeeIndex = matchingFees.findIndex(fee => fee.type === this.getFeeTypeDisplay(config.type));
          const fee: DynamicFeesInfoBlockFee = { type: this.getFeeTypeDisplay(config.type), value: config.value };

          return replaceArrayItem(
            acc,
            matchingSectionIndex,
            { currencyCode: config.currencyCode, fees: replaceArrayItem(matchingFees, matchingFeeIndex, fee) },
          );
        },
        defaultPaymentFees,
      );

      return dynamicFeesInfoBlockContent.map(section => {
        const processingFeeRate = section.fees.find(({ type }) =>
          type === this.getFeeTypeDisplay(PaymentFeeType.CREDIT_CARD_PROCESSING_FEE_RATE),
        ).value;

        return {
          ...section,
          fees: section.fees
            // exclude the payment plan processing fee if it is the same as the processing fee
            .filter(fee =>
              fee.type !== this.getFeeTypeDisplay(PaymentFeeType.PAYMENT_PLAN_CREDIT_CARD_PROCESSING_FEE_RATE)
              || fee.value !== processingFeeRate,
            )
            .sort((a, b) => PAYMENT_FEE_TYPE_ORDER[a.type] - PAYMENT_FEE_TYPE_ORDER[b.type]),
        };
      });
    }),
  );

  isDebitBlockWarningDismissed$ = new BehaviorSubject(localStorage.getItem(HAS_USER_DISMISSED_DEBIT_BLOCK_WARNING) === 'true');
  shouldShowAchDebitBlockWarning$ = combineLatest([
    this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_DEBIT_BLOCK_WARNING),
    this.isDebitBlockWarningDismissed$,
  ]).pipe(
    switchMap(([enabled, isDebitBlockWarningDismissed]) => !enabled || isDebitBlockWarningDismissed ? of(false) : this.isAchSelected$),
  );

  // Set to true once a terminal PAYMENT_METHOD_* event has fired (ADD_COMPLETE).
  // afterClosed reads this to decide whether to emit PAYMENT_METHOD_ADD_CANCELLED.
  private _didEmitTerminalEvent = false;

  // Saved reference to the unpatched Element.prototype.scrollIntoView. While the dialog is open,
  // the prototype is patched to no-op for Stripe's private iframes — see _patchScrollIntoViewForStripeIframes.
  private _originalScrollIntoView?: typeof Element.prototype.scrollIntoView;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: NewPaymentMethodDialogData,
    readonly newPaymentMethodDialog: MatDialogRef<NewPaymentMethodDialogComponent, NewPaymentMethodDialogResult>,
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly store: Store,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly authAdaptor: AuthAdaptorService,
    private readonly host: ElementRef<NewPaymentMethodDialogComponent & HTMLElement>,
    private readonly logger: Logger,
    private readonly authStateAdaptor: AuthStateAdaptorService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly configService: ConfigService,
  ) {
    this._patchScrollIntoViewForStripeIframes();
    // Set envelope first so every PAYMENT_METHOD_* event emitted from this dialog instance
    // (including the ADD_STARTED fire below and the cancel path) carries the right metadata.
    this.paymentMethodTrackService.setSurface(this.data.surface);
    this.paymentMethodTrackService.setIntent(this.data.intent);
    this.paymentMethodTrackService.setPaymentFlow('paymentFlow' in this.data ? this.data.paymentFlow : undefined);
    this.paymentMethodTrackService.setUserType(this.authStateAdaptor.getUserTypeSnapshot());
    this.paymentMethodTrackService.setIsFirstPaymentMethod(!this.data.userHasSavedPaymentMethods);

    // Fires exactly once per dialog open, regardless of opener. Keeps the funnel-entry count
    // accurate even when multiple call sites open this dialog.
    this.paymentMethodTrackService.trackWorkflow(PaymentMethodWorkflowEvent.PAYMENT_METHOD_ADD_STARTED);

    // Surface platform-init failures as a discrete attach-time-precursor error. Stripe's
    // `loadStripe()` rejecting (ad-blocker / CSP / network) leaves the dialog rendered but
    // with an empty Stripe element area — without this, the failure is silent in Pendo.
    this.paymentPlatformService.getPlatformInitErrors().pipe(
      take(1),
      takeUntil(this._unsubscribe$),
    ).subscribe(error => this.paymentMethodTrackService.trackWorkflow(
      PaymentMethodWorkflowEvent.PAYMENT_METHOD_PLATFORM_INIT_FAILED,
      extractFailureContext(error),
    ));

    this.setPaymentMethodModeAsNew$.subscribe();
    this.manageCheckboxStates$.subscribe();
    this.trackPaymentMethodTypeSelection$.subscribe();

    // Catches every dismiss path (Cancel button, X, ESC, backdrop) in one place.
    // `withLatestFrom` captures the current type at afterClosed time — the type observable is
    // an NGXS selector that emits the current value synchronously on subscribe, so this works
    // even if afterClosed and ngOnDestroy reorder in a future Angular Material upgrade
    // (ngOnDestroy resets the type back to undefined). When the user dismisses before picking
    // a type, the value stays `undefined` and we omit the field rather than coerce it.
    this.newPaymentMethodDialog.afterClosed().pipe(
      take(1),
      withLatestFrom(this.paymentPlatformService.getNewPaymentMethodType()),
    ).subscribe(([, paymentMethodType]) => {
      if (!this._didEmitTerminalEvent) {
        this.paymentMethodTrackService.trackWorkflow(
          PaymentMethodWorkflowEvent.PAYMENT_METHOD_ADD_CANCELLED,
          paymentMethodType != undefined
            ? { paymentMethodType: paymentMethodType.toString().toUpperCase() }
            : {},
        );
      }
    });

    if (this.authStateAdaptor.getUserTypeSnapshot() === UserType.GUEST) {
      return;
    }

    if (this.isSaveRequired()) {
      this.store.dispatch(new SetShouldSaveNewPaymentMethod(true));
    }
  }

  /**
   * Track PAYMENT_METHOD_TYPE_SELECTED whenever the user picks a new payment method type while the
   * add dialog is open. The dialog clears `newPaymentMethodType` on destroy, so each open of the
   * dialog starts fresh.
   */
  private readonly trackPaymentMethodTypeSelection$ = this.paymentPlatformService.getNewPaymentMethodType().pipe(
    ignoreNullish(),
    distinctUntilChanged(),
    tap(paymentMethodType => this.paymentMethodTrackService.trackWorkflow(
      PaymentMethodWorkflowEvent.PAYMENT_METHOD_TYPE_SELECTED,
      { paymentMethodType: (paymentMethodType ?? '').toString().toUpperCase() },
    )),
    takeUntil(this._unsubscribe$),
  );

  onNavigateToDebitBlockHelpPage(): void {
    window.open('https://www.clientsupport.aiwyn.ai/hc/en-us/articles/6467585256091-Failed-ACH-Payment', '_blank', 'noopener noreferrer');
  }

  onDismissDebitBlockWarning(): void {
    localStorage.setItem(HAS_USER_DISMISSED_DEBIT_BLOCK_WARNING, 'true');
    this.isDebitBlockWarningDismissed$.next(true);
  }

  isSaveRequired(): boolean {
    return this.data.intent === PaymentMethodIntent.SETUP || this.data.paymentFlow === PaymentFlow.PLAN;
  }

  paymentMethodError$ = new Subject<PaymentMethodError>();
  paymentMethodCreationLoading$: Subject<boolean> = new Subject<boolean>();
  createPaymentMethod(): void {
    this.paymentMethodCreationLoading$.next(true);
    const attachKey = 'PAYMENT_METHOD_ATTACH';

    // Snapshot the submit-time intent before kicking off platform validation so VALIDATION_SUBMITTED
    // reflects the user's choices at submit, not at attach (the existing post-validation chain reads
    // shouldSave/shouldSetAsDefault again — those reads return the same selectOnce value).
    combineLatest([
      this.paymentPlatformService.getNewPaymentMethodType(),
      this.store.selectOnce(PaymentsState.getShouldSaveNewPaymentMethod),
      this.store.selectOnce(PaymentsState.getShouldSetNewPaymentMethodAsDefault),
    ]).pipe(
      take(1),
      tap(([paymentMethodType, shouldSave, shouldSetAsDefault]) => {
        this.paymentMethodTrackService.trackWorkflow(
          PaymentMethodWorkflowEvent.PAYMENT_METHOD_VALIDATION_SUBMITTED,
          {
            paymentMethodType: paymentMethodType.toString().toUpperCase(),
            shouldSave,
            shouldSetAsDefault,
          },
        );
        this.paymentMethodTrackService.startOperation(attachKey);
      }),
      switchMap(() => this.paymentPlatformService.createPaymentMethod()),
      tap(createdPaymentMethod => this.paymentMethodTrackService.trackWorkflow(
        PaymentMethodWorkflowEvent.PAYMENT_METHOD_SETUP_CONFIRMED,
        {
          paymentMethodId: createdPaymentMethod.id,
          paymentMethodType: (createdPaymentMethod?.subType ?? createdPaymentMethod?.type ?? '').toString().toUpperCase(),
          // Only ACH methods carry a connected-vs-manual signal; omit for card / ACSS / BACS.
          ...(createdPaymentMethod.isConnectedAccount !== undefined
            ? { isConnectedAccount: createdPaymentMethod.isConnectedAccount }
            : {}),
        },
      )),
      switchMap(createdPaymentMethod => combineLatest([
        this.store.selectOnce(PaymentsState.getShouldSaveNewPaymentMethod),
        this.store.selectOnce(PaymentsState.getShouldSetNewPaymentMethodAsDefault),
      ]).pipe(
        switchMap(([shouldSave, shouldSetAsDefault]) => {
          const isSavingFirstPaymentMethod = shouldSave && !this.data.userHasSavedPaymentMethods;
          const paymentMethodType = (createdPaymentMethod?.subType ?? createdPaymentMethod?.type ?? '').toString().toUpperCase();

          const onSubmitActions$ = [
            ...(shouldSave
              ? [this.paymentPlatformService.savePaymentMethod(createdPaymentMethod.id).pipe(
                tap(_ => this.paymentMethodTrackService.trackWorkflow(
                  PaymentMethodWorkflowEvent.PAYMENT_METHOD_ATTACH_SUCCEEDED,
                  {
                    paymentMethodId: createdPaymentMethod.id,
                    paymentMethodType,
                    operationDurationMs: this.paymentMethodTrackService.endOperation(attachKey),
                  },
                )),
              )]
              : []),
            ...(
              shouldSetAsDefault || isSavingFirstPaymentMethod
                ? [this.paymentPlatformService.setDefaultPaymentMethod(createdPaymentMethod.id).pipe(
                  tap(_ => this.paymentMethodTrackService.trackWorkflow(
                    PaymentMethodWorkflowEvent.PAYMENT_METHOD_SET_DEFAULT_SUCCEEDED,
                    {
                      paymentMethodId: createdPaymentMethod.id,
                      paymentMethodType,
                      // setDefault is a piggy-back leg of the attach flow — there's no dedicated
                      // start marker for it, so stamp the elapsed attach duration as a best-effort
                      // signal of "how long did this whole add session take to get here."
                      operationDurationMs: 0,
                    },
                  )),
                )]
                : []
            ),
          ];

          return combineLatest(onSubmitActions$.length > 0 ? onSubmitActions$ : [of(undefined)]).pipe(
            take(1),
            tap(_ => {
              this._didEmitTerminalEvent = true;
              this.paymentMethodTrackService.trackWorkflow(
                PaymentMethodWorkflowEvent.PAYMENT_METHOD_ADD_COMPLETE,
                { paymentMethodId: createdPaymentMethod.id, paymentMethodType, isTemporary: !shouldSave },
              );
              this.onClose({ createdPaymentMethod, isTemporary: !shouldSave });
            }),
          );
        }),
      )),
      tap({
        // Stripe.js client-side rejections carry `code` / `decline_code` directly on the error
        // (see NewPaymentMethodFailedComponent's display switch for the same field names). The
        // attach HTTP leg (`savePaymentMethod`) surfaces an `HttpErrorResponse` whose status is
        // captured into `FailureContext.errorCode` via extractFailureContext. Aiwyn-side validation
        // failures (duplicate card, restricted state, etc.) come through as
        // `{ message: AIWYN_PAYMENT_METHOD_VALIDATION_ERROR_MESSAGE, validationErrors }` —
        // those become `errorCategory: 'aiwyn-validation'` with the failing rule key on
        // `errorSubcategory`. Stripe codes are best-effort: any field absent on a given error
        // leaves the corresponding optional metadata field undefined.
        error: error => {
          const validationKey = this._pickValidationError(error?.validationErrors);
          const baseFailureContext = extractFailureContext(error);
          const failureContext = validationKey != undefined
            ? { ...baseFailureContext, errorCategory: AIWYN_VALIDATION_ERROR_CATEGORY, errorSubcategory: validationKey }
            : baseFailureContext;
          this.paymentMethodTrackService.trackWorkflow(
            PaymentMethodWorkflowEvent.PAYMENT_METHOD_ATTACH_FAILED,
            {
              ...failureContext,
              operationDurationMs: this.paymentMethodTrackService.endOperation(attachKey),
              stripeDeclineCode: error?.decline_code,
              stripeErrorCode: error?.code,
            },
          );
        },
      }),
      catchError(error => this._canOfferExistingMethodReuse(error)
        ? this._offerExistingMethodReuse(error)
        : this._showInlineValidationError(error)),
      take(1),
      takeUntil(this._unsubscribe$),
      finalize(() => this.paymentMethodCreationLoading$.next(false)),
    ).subscribe();
  }

  /**
   * In the pay flow, Stripe Link re-surfaces a bank account / card the buyer already has saved into
   * the "add new method" element. The duplicate guard then fires and Continue dead-ends on the inline
   * "Duplicate payment method" error (see T3-3893). Rather than hard-block, offer a one-click recovery:
   * confirm and reuse the already-saved method. Only in `PAYMENT` intent — in `SETUP` (settings → Saved
   * Payment Methods) the user is explicitly managing their wallet, so there is nothing to "use" and the
   * inline error is the correct response.
   */
  private _canOfferExistingMethodReuse(error: PaymentMethodError | undefined): boolean {
    return this.data.intent === PaymentMethodIntent.PAYMENT
      && this._pickValidationError(error?.validationErrors) === PaymentMethodValidationError.DUPLICATE
      && error?.paymentMethod instanceof AbstractPaymentMethod;
  }

  private _offerExistingMethodReuse(error: PaymentMethodError): Observable<unknown> {
    const createdPaymentMethod = error.paymentMethod as AbstractPaymentMethod;

    return this.paymentPlatformService.getSavedPaymentMethods().pipe(
      take(1),
      switchMap(savedPaymentMethods => {
        const existingPaymentMethod = savedPaymentMethods.find(savedPaymentMethod => createdPaymentMethod.equals(savedPaymentMethod));

        // No saved match to fall back on (shouldn't happen for a DUPLICATE, but stay safe) — keep the inline error.
        if (existingPaymentMethod == undefined) {
          return this._showInlineValidationError(error);
        }

        return this._confirmExistingMethodReuse(existingPaymentMethod).pipe(
          switchMap(confirmed => {
            if (!confirmed) {
              return this._showInlineValidationError(error);
            }

            this._didEmitTerminalEvent = true;
            // `reusedExisting: true` marks this as the duplicate-recovery completion — the dialog
            // resolved to an already-saved method, so no ATTACH_SUCCEEDED preceded it (see T3-3893).
            // `paymentMethodType` is derived from the reused method the same way the normal add path
            // derives it from the freshly-created one, so ADD_COMPLETE always carries the type.
            const paymentMethodType = (existingPaymentMethod.subType ?? existingPaymentMethod.type ?? '').toString().toUpperCase();
            this.paymentMethodTrackService.trackWorkflow(
              PaymentMethodWorkflowEvent.PAYMENT_METHOD_ADD_COMPLETE,
              { paymentMethodId: existingPaymentMethod.id, paymentMethodType, isTemporary: false, reusedExisting: true },
            );
            // Resolve the dialog to the already-saved method, exactly as if it had just been added —
            // openers consume afterClosed() → NewPaymentMethodDialogResult and proceed to select + pay.
            this.onClose({ createdPaymentMethod: existingPaymentMethod, isTemporary: false });
            return of(undefined);
          }),
        );
      }),
    );
  }

  private _confirmExistingMethodReuse(existingPaymentMethod: AbstractPaymentMethod): Observable<boolean> {
    const noun = this._existingPaymentMethodNoun(existingPaymentMethod);

    return this.dialog.open<ConfirmationModalComponent, ConfirmationPayload, boolean>(ConfirmationModalComponent, {
      data: {
        title: `This ${noun} is already one of your saved payment methods.`,
        message: `Do you want to use the existing ${noun}?`,
        confirmButtonText: `Use this ${noun}`,
        confirmButtonColor: 'primary',
        cancelButtonText: 'Enter a different one',
      },
      backdropClass: 'confirmation-dialog-backdrop',
    }).afterClosed().pipe(
      take(1),
      map(confirmed => confirmed === true),
    );
  }

  /** Derive the buyer-facing noun for the reuse copy from the method's category. */
  private _existingPaymentMethodNoun(paymentMethod: AbstractPaymentMethod): string {
    switch (PAYMENT_METHOD_TYPE_TO_CATEGORY[paymentMethod.type]) {
      case PAYMENT_METHOD_CATEGORY.BANK_DEBIT:
        return 'bank account';
      case PAYMENT_METHOD_CATEGORY.CARD:
        return 'card';
      default:
        return 'payment method';
    }
  }

  private _showInlineValidationError(error: PaymentMethodError): Observable<unknown> {
    this.paymentMethodError$.next(error);
    this.changeDetectorRef.detectChanges();

    // drill into dialog component to scroll to top of content
    this.host.nativeElement.firstElementChild.firstElementChild.firstElementChild.nextElementSibling.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    return this.paymentPlatformService.onInvalidPaymentMethodCreation(error as unknown as Error);
  }

  dismissValidationErrorMessage(): void {
    this.paymentMethodError$.next(undefined);
    this.changeDetectorRef.detectChanges();
  }

  onClose(dialogResult?: NewPaymentMethodDialogResult): void {
    this.paymentPlatformService.getNewPaymentMethodType().pipe(
      take(1),
      tap(this._logPaymentMethod(dialogResult)),
    ).subscribe();

    this.store.dispatch(new ClearNewPaymentMethodData()).pipe(
      switchMap(_ => this.platformSelectNewPaymentMethodTemplate.first.onClose()),
      switchMap(_ =>
        dialogResult?.createdPaymentMethod != undefined
          ? this.paymentPlatformService.onValidPaymentMethodCreation(dialogResult.createdPaymentMethod)
          : of(undefined),
      ),
      take(1),
      tap(_ => this.newPaymentMethodDialog.close(dialogResult)),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  private _pickValidationError(validationErrors: PaymentMethodValidationErrors | undefined): PaymentMethodValidationError | undefined {
    if (validationErrors == undefined) {
      return undefined;
    }
    return PAYMENT_METHOD_VALIDATION_ERROR_PRECEDENCE.find(key => key in validationErrors);
  }

  /**
   * @deprecated Superseded by the `PaymentMethodWorkflowEvent` taxonomy. The workflow events
   * (`PAYMENT_METHOD_ADD_COMPLETE`, `PAYMENT_METHOD_ADD_CANCELLED`) carry strictly more information
   * than these three ad-hoc events — `paymentMethodId`, `paymentMethodType`, `isTemporary`, plus
   * the full `PaymentMethodEventBaseMetadata` envelope. Retiring is blocked on dashboard-owner
   * confirmation that no legacy Pendo dashboard or LogRocket session-filter keys on the
   * `'Card Payment Method' | 'Bank Debit Payment Method' | 'Wallet Payment Method'` event names.
   *
   * Logs payment method events for analytics and comparison purposes.
   * This function is used exclusively for logging and does not affect application behavior.
   *
   * Expected events:
   * - "Card Payment Method" - Tracks credit/debit card payment methods
   *   - Properties: { type: "card", creation: "completed" | "cancelled" }
   *
   * - "Bank Debit Payment Method" - Tracks bank account payment methods
   *   - Properties: { type: "us_bank_account" | "ach" | "acss_debit" | "bacs" | "bacs_debit", creation: "completed" | "cancelled" }
   *
   * - "Wallet Payment Method" - Tracks digital wallet payment methods
   *   - Properties: { type: "apple_pay" | "google_pay", creation: "completed" | "cancelled" }
   *
   * @param dialogResult - The result from the dialog; undefined if cancelled, contains payment method if completed
   * @returns A function that logs the payment method event when invoked with a payment method type
   */
  private _logPaymentMethod(dialogResult?: NewPaymentMethodDialogResult): (newPaymentMethodType: PaymentMethodType) => void {
    return (newPaymentMethodType: PaymentMethodType) => {
      const category = PAYMENT_METHOD_TYPE_TO_CATEGORY[newPaymentMethodType];
      const displayName = PAYMENT_METHOD_CATEGORY_LOG_NAME[category];
      const creationStatus = isEmpty(dialogResult) ? 'cancelled' : 'completed';

      this.legacyTrackService.track(`${displayName} Payment Method`, {
        type: newPaymentMethodType,
        creation: creationStatus
      });
    }
  }

  ngOnDestroy(): void {
    this._restoreScrollIntoView();

    this.paymentPlatformService.setNewPaymentMethodType(undefined).pipe(take(1)).subscribe();

    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  /**
   * Stripe's Payment Element renders each input (card number, expiry, CVC, postal) inside its own
   * cross-origin iframe whose `name` starts with `__privateStripeFrame`. When a password manager
   * (e.g. 1Password) autofills, it focuses each iframe in rapid succession; Stripe's parent-side
   * controller then calls `iframe.scrollIntoView()` on each focus. Because the dialog body is the
   * closest scrollable ancestor and the iframes span its top and bottom, the dialog scroll
   * position oscillates rapidly and the form becomes effectively unusable.
   *
   * While the dialog is open we patch the prototype so calls scrolling a Stripe iframe become
   * no-ops. The original is restored on destroy. All other scrollIntoView callers are unaffected.
   */
  private _patchScrollIntoViewForStripeIframes(): void {
    const original = Element.prototype.scrollIntoView;
    this._originalScrollIntoView = original;

    Element.prototype.scrollIntoView = function (arg?: boolean | ScrollIntoViewOptions): void {
      if (this instanceof HTMLIFrameElement && this.name?.startsWith('__privateStripeFrame')) {
        return;
      }
      return original.call(this, arg);
    };
  }

  private _restoreScrollIntoView(): void {
    if (this._originalScrollIntoView != null) {
      Element.prototype.scrollIntoView = this._originalScrollIntoView;
      this._originalScrollIntoView = undefined;
    }
  }

  readonly SECURE_ACH_INFO_MESSAGES = SECURE_ACH_INFO_MESSAGES;
}

export class NewPaymentMethodDialogDataMock {
  protected _model: NewPaymentMethodDialogData;

  private readonly _paymentModel: NewPaymentMethodDialogPaymentData = {
    intent: PaymentMethodIntent.PAYMENT,
    userHasSavedPaymentMethods: true,
    paymentFlow: PaymentFlow.STANDARD,
    surface: 'payments-sidebar-invoices',
  };

  private readonly _setupModel: NewPaymentMethodDialogSetupData = {
    intent: PaymentMethodIntent.SETUP,
    userHasSavedPaymentMethods: true,
    surface: 'settings-payment-methods',
  };

  constructor(data?: Partial<NewPaymentMethodDialogSetupData>, intent?: PaymentMethodIntent.SETUP);
  constructor(data?: Partial<NewPaymentMethodDialogPaymentData>, intent?: PaymentMethodIntent.PAYMENT);
  constructor(data: Partial<NewPaymentMethodDialogData> = {}, intent: PaymentMethodIntent = PaymentMethodIntent.SETUP) {
    const _desiredModel = intent === PaymentMethodIntent.SETUP ? this._setupModel : this._paymentModel;

    this._model = {
      ..._desiredModel,
      ...data,
    } as NewPaymentMethodDialogData;
  }

  public model(): NewPaymentMethodDialogData {
    return this._model;
  }
}
